import React, { useState } from 'react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

// Inline markdown: ***bold+italic***, **bold**, *italic*. Preserves original
// whitespace so line breaks in the textarea render as-is inside the page.
const parseInlineText = (text) => {
  if (!text) return [];
  const segments = [];
  const regex = /\*\*\*([^*\n]+?)\*\*\*|\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    if (m[1] !== undefined) segments.push({ type: 'bolditalic', value: m[1] });
    else if (m[2] !== undefined) segments.push({ type: 'bold', value: m[2] });
    else if (m[3] !== undefined) segments.push({ type: 'italic', value: m[3] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
};

const BULLET_RE = /^[-•]\s+(.*)$/;
const NUMBERED_RE = /^(\d+)[.)]\s+(.*)$/;

// Groups lines into blocks: consecutive "- " lines → bullet list, consecutive
// "1. " lines → numbered list, everything else → plain text (newlines kept).
const parseBlocks = (text) => {
  const blocks = [];
  (text || '').split('\n').forEach((line) => {
    const bullet = line.match(BULLET_RE);
    const numbered = !bullet && line.match(NUMBERED_RE);
    const last = blocks[blocks.length - 1];
    if (bullet || numbered) {
      const type = bullet ? 'bullet' : 'numbered';
      const item = bullet
        ? { marker: '•', text: bullet[1] }
        : { marker: `${numbered[1]}.`, text: numbered[2] };
      if (last && last.type === type) last.items.push(item);
      else blocks.push({ type, items: [item] });
    } else if (last && last.type === 'text') {
      last.lines.push(line);
    } else {
      blocks.push({ type: 'text', lines: [line] });
    }
  });
  return blocks;
};

const renderInline = (text) =>
  parseInlineText(text).map((seg, idx) => {
    if (seg.type === 'bold') return <strong key={idx}>{seg.value}</strong>;
    if (seg.type === 'italic') return <em key={idx}>{seg.value}</em>;
    if (seg.type === 'bolditalic') return <strong key={idx}><em>{seg.value}</em></strong>;
    return <React.Fragment key={idx}>{seg.value}</React.Fragment>;
  });

// Book. Closed by default — click the cover to open, click a right page to flip
// forward, click a left page to flip back. Leaves are absolutely-positioned on
// the right half of the container and rotate 180° around the left edge (spine),
// so the front face travels from the right pane to the left pane during flip.
//
// Content model: `pages` is a flat array of single-face pages. Leaf 0 is a
// dedicated cover leaf (front = cover art, back = pages[0]). Leaf i>=1 holds
// pages[2i-1] on the front and pages[2i] on the back — matching how paper
// leaves work in a physical book.
const BookElement = ({
  element,
  isEditor = false,
  canInteract = true,
  syncedFlippedCount = null,
  onFlippedCountChange = null,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { content = {} } = element;
  const {
    cover = {},
    pages = [],
    pageColor = '#FDF6E3',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 18,
  } = content;

  const coverBg = cover.bgColor || '#8B4513';
  const coverTextColor = cover.textColor || '#F5DEB3';
  const coverText = cover.text ?? t('editor.book.coverDefault', { defaultValue: 'Книга' });
  const coverFontSize = Number.isFinite(cover.fontSize) ? cover.fontSize : 28;
  const coverFontFamily = cover.fontFamily || fontFamily;

  const totalLeaves = Math.max(1, Math.ceil((pages.length + 1) / 2));
  // After flipping k leaves the open spread is pages[2k-2] | pages[2k-1].
  // Stop at the last spread that still has content — otherwise flipping the
  // final leaf leaves an empty left (its blank back) and empty right page.
  const maxFlipped = Math.max(1, Math.min(totalLeaves, Math.ceil(pages.length / 2)));

  const isControlled = typeof syncedFlippedCount === 'number';
  const [localFlipped, setLocalFlipped] = useState(0);
  const rawFlipped = isControlled ? syncedFlippedCount : localFlipped;
  const flippedCount = Math.max(0, Math.min(maxFlipped, rawFlipped));

  const canFlip = !isEditor && !disabled && canInteract;

  // Asymmetric fade timing: on open, delay the reveal until the cover has
  // rotated past 90° (so the next spread doesn't peek through early); on close,
  // fade out immediately so the pages are gone before the cover rotates back
  // over them (otherwise the page shows behind the cover during closing).
  const opacityTransition = flippedCount > 0
    ? 'opacity 300ms ease 450ms'
    : 'opacity 200ms ease 0ms';

  const setFlipped = (next) => {
    const clamped = Math.max(0, Math.min(maxFlipped, next));
    if (isControlled) {
      onFlippedCountChange?.(clamped);
    } else {
      setLocalFlipped(clamped);
      onFlippedCountChange?.(clamped);
    }
  };

  const handleFlipNext = () => {
    if (!canFlip || flippedCount >= maxFlipped) return;
    setFlipped(flippedCount + 1);
  };

  const handleFlipPrev = () => {
    if (!canFlip || flippedCount <= 0) return;
    setFlipped(flippedCount - 1);
  };

  const getLeafFaces = (leafIdx) => {
    if (leafIdx === 0) {
      return { front: { kind: 'cover' }, back: pages[0] || null };
    }
    return {
      front: pages[2 * leafIdx - 1] || null,
      back: pages[2 * leafIdx] || null,
    };
  };

  const renderPageContent = (page) => {
    if (!page) return null;
    const isImage = page.type === 'image' && page.imageUrl;
    if (isImage) {
      return (
        <img
          src={resolveMediaUrl(page.imageUrl)}
          alt=""
          className="max-w-full max-h-full object-contain"
          draggable={false}
        />
      );
    }
    const align = page.align === 'left' || page.align === 'right' ? page.align : 'center';
    const blocks = parseBlocks(page.text || '');
    return (
      <div
        className="break-words w-full"
        style={{
          color: textColor,
          fontFamily,
          fontSize: `${fontSize}px`,
          lineHeight: 1.4,
          textAlign: align,
        }}
      >
        {blocks.map((block, bIdx) => {
          if (block.type === 'text') {
            return (
              <div key={bIdx} style={{ whiteSpace: 'pre-wrap' }}>
                {renderInline(block.lines.join('\n'))}
              </div>
            );
          }
          // List keeps its markers left-aligned; the block itself follows page alignment.
          return (
            <div key={bIdx} style={{ display: 'inline-block', textAlign: 'left', maxWidth: '100%' }}>
              {block.items.map((item, iIdx) => (
                <div key={iIdx} style={{ display: 'flex', gap: '0.4em' }}>
                  <span style={{ flexShrink: 0, minWidth: block.type === 'numbered' ? '1.2em' : undefined }}>
                    {item.marker}
                  </span>
                  <span style={{ whiteSpace: 'pre-wrap', minWidth: 0 }}>{renderInline(item.text)}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCoverContent = () => {
    if (cover.imageUrl) {
      return (
        <img
          src={resolveMediaUrl(cover.imageUrl)}
          alt=""
          className="max-w-[80%] max-h-[80%] object-contain"
          draggable={false}
        />
      );
    }
    return (
      <p
        className="text-center break-words"
        style={{
          color: coverTextColor,
          fontFamily: coverFontFamily,
          fontSize: `${coverFontSize}px`,
          fontWeight: 700,
          lineHeight: 1.2,
          padding: '10%',
          margin: 0,
        }}
      >
        {coverText}
      </p>
    );
  };

  const renderFace = ({ face, side, isCoverFace }) => {
    const isLeft = side === 'left';
    const bg = isCoverFace ? coverBg : pageColor;
    return (
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        style={{
          background: bg,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          borderRadius: isLeft ? '8px 0 0 8px' : '0 8px 8px 0',
          boxShadow: isLeft
            ? 'inset -6px 0 12px -6px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.15)'
            : 'inset 6px 0 12px -6px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.15)',
          padding: isCoverFace ? 0 : '5%',
          transform: isLeft ? 'rotateY(180deg)' : 'none',
        }}
      >
        {isCoverFace ? renderCoverContent() : renderPageContent(face)}
      </div>
    );
  };

  return (
    <div
      className="relative w-full h-full select-none"
      style={{ perspective: '1800px' }}
    >
      {/* Right page-color surface — always mounted. When closed, leaf 0 (cover)
          fully covers it, so it isn't visible; during the cover flip it becomes
          the paper reveal behind the rotating cover — this is why we don't gate
          it on flippedCount. */}
      <div
        className="absolute right-0 top-0 h-full pointer-events-none"
        style={{
          width: '50%',
          background: pageColor,
          borderRadius: '0 8px 8px 0',
        }}
      />
      {/* Left page-color surface — fades in during the second half of the
          cover flip so a closed book has a transparent left half rather than
          jumping to cream at the moment of click. */}
      <div
        className="absolute left-0 top-0 h-full pointer-events-none"
        style={{
          width: '50%',
          background: pageColor,
          borderRadius: '8px 0 0 8px',
          opacity: flippedCount > 0 ? 1 : 0,
          transition: opacityTransition,
        }}
      />

      {/* Leaves */}
      {Array.from({ length: totalLeaves }, (_, i) => i).map((leafIdx) => {
        const isFlipped = leafIdx < flippedCount;
        const { front, back } = getLeafFaces(leafIdx);

        // z-index: flipped leaves stack on top of each other by flip order
        // (last flipped = topmost on the left). Unflipped leaves reverse — the
        // top-most (next to flip) is leaf === flippedCount.
        const z = isFlipped
          ? totalLeaves + leafIdx
          : totalLeaves - leafIdx;

        const isTopUnflipped = !isFlipped && leafIdx === flippedCount;
        const isTopFlipped = isFlipped && leafIdx === flippedCount - 1;

        let onClick;
        if (isTopUnflipped && canFlip) onClick = handleFlipNext;
        else if (isTopFlipped && canFlip) onClick = handleFlipPrev;

        // Non-cover leaves stay hidden while the book is closed, then fade in
        // during the second half of the cover's rotation. Without this, leaf 1
        // (pages[1]) shows through as soon as the cover leaves rotates away and
        // the reveal looks premature.
        const leafOpacity = leafIdx === 0 || flippedCount > 0 ? 1 : 0;

        return (
          <div
            key={leafIdx}
            className="absolute top-0 right-0 h-full"
            style={{
              width: '50%',
              transformStyle: 'preserve-3d',
              transformOrigin: 'left center',
              transform: isFlipped ? 'rotateY(-180deg)' : 'rotateY(0deg)',
              opacity: leafOpacity,
              transition: `transform 750ms cubic-bezier(0.4, 0, 0.2, 1), ${opacityTransition}`,
              zIndex: z,
              cursor: onClick ? 'pointer' : 'default',
              pointerEvents: onClick && leafOpacity > 0 ? 'auto' : 'none',
            }}
            onClick={onClick}
          >
            {renderFace({
              face: front,
              side: 'right',
              isCoverFace: leafIdx === 0 && front?.kind === 'cover',
            })}
            {renderFace({ face: back, side: 'left', isCoverFace: false })}
          </div>
        );
      })}

      {/* Spine — hidden when closed and faded in with the same delay as the
          left page and non-cover leaves, so the reveal is coordinated. */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{
          left: 'calc(50% - 2px)',
          width: 4,
          zIndex: totalLeaves * 3 + 10,
          background:
            'linear-gradient(to right, rgba(0,0,0,0.25), rgba(0,0,0,0.05), rgba(0,0,0,0.25))',
          opacity: flippedCount > 0 ? 1 : 0,
          transition: opacityTransition,
        }}
      />

      {isEditor && pages.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
          <div className="px-4 py-2 rounded-lg bg-black/60 text-white text-xs font-medium shadow">
            {t('editor.book.editorEmpty', {
              defaultValue: 'Додайте сторінки у панелі властивостей праворуч',
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookElement;
